import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { environment } from '@environments/environment';
import {
  TitleStrategy,
  provideRouter,
  withComponentInputBinding,
  withHashLocation,
} from '@angular/router';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { registerLocaleData } from '@angular/common';
import { LOCALE_ID } from '@angular/core';
import localeEsMx from '@angular/common/locales/es-MX';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';

import { routes } from './app.routes';
import { AppTitleStrategyService } from '@shared/services/app-title-strategy.service';
import { handlerErrorInterceptor } from '@shared/interceptors/handler-error.interceptor';
import { ICON_FONT_SET_CLASSES } from '@shared/models/icon.model';
import { PaginatorIntlService } from '@shared/services/paginator-intl.service';

registerLocaleData(localeEsMx, 'es-MX');

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding(), withHashLocation()),
    provideHttpClient(
      withFetch(),
      withInterceptors([authHttpInterceptorFn, handlerErrorInterceptor]),
    ),
    provideAuth0({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      authorizeTimeoutInSeconds: 10,
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: environment.auth0.audience,
      },
      httpInterceptor: {
        allowedList: environment.urlBase ? [`${environment.urlBase}/*`] : [],
      },
    }),
    provideAppInitializer(() => {
      inject(MatIconRegistry).setDefaultFontSetClass(...ICON_FONT_SET_CLASSES);
    }),
    provideAnimationsAsync(),
    providePrimeNG({ theme: 'none' }),
    MessageService,
    { provide: LOCALE_ID, useValue: 'es-MX' },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: TitleStrategy, useClass: AppTitleStrategyService },
    { provide: MatPaginatorIntl, useClass: PaginatorIntlService },
  ],
};
